import type { Figure } from "@op/puck-types";
import { Dc_Divider, Dc_Media_Figures, Dc_Reading } from "@op/sf-op";
import { Fragment, useMemo } from "react";
import {
  makeEditorImageUrl,
  makeFigureId,
  makeRendererImageUrl,
  MediaPreview,
  useRender,
} from "@op/utils";
// @ts-ignore
import placeholderImage from "../../../../../assets/placeholder.svg?url&no-inline";

export interface ReadingCompanionFiguresProps {
  figures: Figure[];
}

interface FigureCompProps {
  figure: Figure;
}

const FigureComp = ({ figure }: FigureCompProps) => {
  const { context, apiUrl } = useRender();

  const imageUrl = useMemo(() => {
    if (!figure || !figure.uploadId) return "";
    switch (context) {
      case "editor":
        return makeEditorImageUrl(figure.uploadId, apiUrl);
      case "renderer":
        return makeRendererImageUrl(figure.uploadId, apiUrl);
      default:
        return "";
    }
  }, [context, apiUrl, figure]);

  const image = useMemo(() => {
    return (
      <>
        {imageUrl ? (
          <MediaPreview
            src={imageUrl}
            alt={figure.metadata?.caption || ""}
            mimeType={figure.mimeType}
            width={figure.width}
            height={figure.height}
          />
        ) : (
          <MediaPreview
            mimeType="image/svg+xml"
            src={placeholderImage}
            width={600}
            height={600}
          />
        )}
      </>
    );
  }, [imageUrl, figure]);

  const { id, metadata } = figure;

  return (
    <Dc_Media_Figures
      noContainer={true}
      image={image}
      buttonStackSlot={
        <Dc_Media_Figures.ButtonStack
          variant="reading-comp"
          textButton={
            <a href={`#${makeFigureId(id)}`} className="contents">
              <Dc_Media_Figures.TextButton>
                Show in text
              </Dc_Media_Figures.TextButton>
            </a>
          }
          sourceButton={
            !!metadata?.source.url && (
              <a
                href={metadata?.source.url}
                target="_blank"
                className="contents"
              >
                <Dc_Media_Figures.SourceButton>
                  View Source
                </Dc_Media_Figures.SourceButton>
              </a>
            )
          }
        />
      }
    >
      {metadata?.caption || metadata?.source.title ? (
        <>
          {metadata?.caption && (
            <Dc_Media_Figures.Description>
              {metadata.caption}
            </Dc_Media_Figures.Description>
          )}
          {metadata?.source.title && (
            <Dc_Media_Figures.SourceDescription>
              {metadata.source.title}
            </Dc_Media_Figures.SourceDescription>
          )}
        </>
      ) : null}
    </Dc_Media_Figures>
  );
};

export const ReadingCompanionFigures = ({
  figures,
}: ReadingCompanionFiguresProps) => {
  return (
    <Dc_Reading.FigureItemStack>
      {figures.map((figure, index) => {
        const { id } = figure;
        return (
          <Fragment key={`${id}-${index}`}>
            <FigureComp figure={figure} />
            {index !== figures.length - 1 && <Dc_Divider />}
          </Fragment>
        );
      })}
    </Dc_Reading.FigureItemStack>
  );
};
